/**
 * Punto de montaje global del simulador de país para Quality Assurance.
 *
 * "must need validate as quality assurance" — solo se muestra si el usuario
 * tiene rol/flag de QA (o es super admin) validado por Clerk.
 * Usa iplocate (via MCP para ejemplos + API real) para sims auténticos de VPN/privacy.
 *
 * Como admin QA puedes usar este toolbar en producción real (páginas públicas)
 * para simular diferentes países, estados US, provincias AR, ciudades y variantes.
 * El sitio se renderiza exactamente como lo vería un visitor de esa geo (incluyendo
 * efectos VPN si simulate_vpn o simulate_ip).
 */

import { Suspense, lazy } from "react";
import { useLocation } from "react-router-dom";

import { getClerkPublishableKey } from "@/lib/clerkPublishableKey";
import { useIsSuperAdmin, useIsQualityAssurance } from "@/lib/auth";
import { useSessionClaims } from "@/hooks/useSessionClaims";
import { isAdminEmbeddedPreview } from "@/lib/adminLivePreview";

const CLERK_PUBLISHABLE_KEY = getClerkPublishableKey();

const AdminGeoSimulator = lazy(() => import("./AdminGeoSimulator"));

/** Gate interno — solo se monta cuando hay ClerkProvider activo (hooks Clerk seguros). */
function Gate() {
  const location = useLocation();
  // QA o super admin (useIsQualityAssurance ya incluye supers).
  const isQA = useIsQualityAssurance();
  // Fallback claims para super.
  const isSuperViaClaims = useSessionClaims().isSuperAdmin;
  const isSuperViaUser = useIsSuperAdmin();
  const allowed = isQA || isSuperViaClaims || isSuperViaUser;

  if (!allowed) return null;
  // No mostrar dentro de iframes de preview embebidos.
  // PERMITIDO en /admin dashboard: así los admins QA pueden usar el toolbar
  // para simular país/estado y ver "preview real" en los drawers de CountryTemplateAdmin,
  // DualCtaVariantsAdmin, etc.
  if (isAdminEmbeddedPreview()) return null;

  return (
    <Suspense fallback={null}>
      <AdminGeoSimulator />
    </Suspense>
  );
}

export default function AdminGeoSimulatorMount() {
  // Sin publishable key AppClerkProvider hace passthrough (sin provider): NO
  // invocar hooks Clerk o explotan. Devolvemos null antes de cualquier hook.
  if (!CLERK_PUBLISHABLE_KEY) return null;
  return <Gate />;
}
